// src/stores/leadStore.js
//
// Store for the Lead Pipeline dashboard, and the "every open lead" half of the search population (customer search,
// link-to-customer, chat matches, pending referrals, the Studio's customer search read dataStore ∪ leadStore).
//
// Who is a lead is the CRM's one rule, the journey stamp (docs/features/one_lead_rule_FEATURE.md; Erik, 2026-10-09:
// "Every part of the site should be using the same rule."). Two populations, each through the user's visibility tiers
// (src/utils/customerVisibilityScope.js, the Command Board's shape):
//   leads           system.journey.section == 'lead' (estimate or not; never signed, a build partner's job, hidden)
//   archived leads  hidden unsigned customers: section 'closed', closedReason 'hidden', contractSigned false (the
//                   dashboard's "show archived" view, unarchive and delete)
// One listener per population and tier filter set. Each listener holds its own full result and the store merges them
// by id, so a change seen by one listener can never drop a row another still holds (the old debounced docChanges
// processor kept only the latest call's batch).

import { defineStore } from 'pinia'
import { db } from '@/services/firebaseconfig'
import { ref, computed } from 'vue'
import { collection, query, where, doc, updateDoc, serverTimestamp } from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { useUserStore } from '@/stores/userStore'
import { usePermissionsStore } from '@/stores/permissionsStore'
import { useCustomerWorkflowsStore } from '@/stores/customerWorkflowsStore'
import { useWorkflowTasksStore } from '@/stores/workflowTasksStore'
import { useAuthConfigStore } from '@/stores/authConfigStore'
import { findTaskStateEntry } from '@/utils/taskUtils'
import { customerScopeFilterSets } from '@/utils/customerVisibilityScope'
import { LEAD_SECTION } from '@/utils/leadRule'

const POPULATIONS = Object.freeze([
  { key: 'leads', filters: () => [where('system.journey.section', '==', LEAD_SECTION)] },
  {
    key: 'archived',
    filters: () => [
      where('system.journey.section', '==', 'closed'),
      where('system.journey.closedReason', '==', 'hidden'),
      where('system.journey.contractSigned', '==', false),
    ],
  },
])

// The workflow summary a lead row shows: a resumed listener re-delivers every lead as "added", and one counts as
// changed only when this moved while the tab slept (the dataStore's rule).
const workflowSummaryMoved = (prev, next) =>
  prev.workflow?.nextTaskId !== next.workflow?.nextTaskId ||
  prev.workflow?.nextTaskAction !== next.workflow?.nextTaskAction ||
  prev.workflow?.currentStageId !== next.workflow?.currentStageId

export const useLeadStore = defineStore('leadStore', () => {
  // State
  const leadsById = ref({})
  const leads = computed(() => Object.values(leadsById.value))
  // Leads whose doc changed in the latest snapshot: the dataStore's modifiedCustomerIds, for the Lead Dashboard.
  // customerWorkflowsStore holds one-time reads and every board write touches the customer doc, so the page re-reads
  // these leads' workflows to show the result (docs/bugs/lead_dashboard_board_unhandled_row_events_BUG.md).
  const modifiedLeadIds = ref([])

  const isLoading = ref(false)
  const error = ref(null)
  const lastFetched = ref(null)
  const hasInitialData = ref(false)

  let unsubscribeLeads = null
  let isFetchingOrSubscribed = ref(false)
  let visibilityHandler = null
  // Grace-delayed tab sleep: pending teardown timer + whether it fired
  let hiddenTeardownTimer = null
  let tornDownWhileHidden = false

  const userStore = useUserStore()

  function clearLeads() {
    leadsById.value = {}
    isLoading.value = false
    isFetchingOrSubscribed.value = false
  }

  // Main fetch function: resolves once every listener has delivered its first snapshot.
  const fetchLeads = (forceRefetch = false) => {
    if (isFetchingOrSubscribed.value && !forceRefetch) {
      return Promise.resolve()
    }
    isFetchingOrSubscribed.value = true

    const perfLabel = `[PERF] [leadStore] fetchLeads total`
    console.time(perfLabel)

    return new Promise((resolve, reject) => {
      ;(async () => {
        // Only show spinner on initial load
        if (!hasInitialData.value) {
          isLoading.value = true
        }
        error.value = null

        try {
          if (unsubscribeLeads) unsubscribeLeads()
          unsubscribeLeads = null

          const user = userStore.user
          if (!user) {
            clearLeads()
            console.timeEnd(perfLabel)
            resolve()
            return
          }

          // Hydrate access-control before the tier check: hasFullVisibility() returns false for every role until
          // system/accessControl loads, which would drop a full-visibility user into the no-access tier.
          // (bug: docs/bugs/mobile_customer_list_empty_BUG.md)
          const permStore = usePermissionsStore()
          await permStore.waitForInit()
          const { filterSets } = customerScopeFilterSets({
            user,
            roles: userStore.roles,
            isSuperAdmin: userStore.isSuperAdmin,
            permStore,
          })
          if (!filterSets.length) {
            clearLeads()
            console.timeEnd(perfLabel)
            resolve()
            return
          }

          const parts = []
          const delivered = new Set()
          let settled = false
          const settle = (fn, value) => {
            if (settled) return
            settled = true
            console.timeEnd(perfLabel)
            fn(value)
          }
          const listeners = []
          for (const population of POPULATIONS) {
            for (const scope of filterSets) {
              const i = parts.length
              parts.push({})
              listeners.push(
                onSnapshot(
                  query(collection(db, 'customers'), ...population.filters(), ...scope),
                  snapshot => {
                    const previous = leadsById.value
                    const next = {}
                    snapshot.forEach(d => {
                      next[d.id] = { id: d.id, ...d.data() }
                    })
                    const modified = snapshot
                      .docChanges()
                      .filter(
                        ch =>
                          ch.type === 'modified' ||
                          (ch.type === 'added' &&
                            previous[ch.doc.id] &&
                            workflowSummaryMoved(previous[ch.doc.id], next[ch.doc.id]))
                      )
                      .map(ch => ch.doc.id)
                    parts[i] = next
                    leadsById.value = Object.assign({}, ...parts)
                    if (modified.length) modifiedLeadIds.value = modified
                    lastFetched.value = Date.now()
                    delivered.add(i)
                    if (delivered.size === listeners.length) {
                      hasInitialData.value = true
                      isLoading.value = false
                      settle(resolve)
                    }
                  },
                  err => {
                    console.error(`❌ [leadStore] ${population.key} snapshot error:`, err)
                    error.value = err
                    isLoading.value = false
                    isFetchingOrSubscribed.value = false
                    settle(reject, err)
                  }
                )
              )
            }
          }
          unsubscribeLeads = () => listeners.forEach(unsub => unsub())
        } catch (err) {
          console.error('❌ [leadStore] Fetch leads error:', err)
          error.value = err
          isLoading.value = false
          isFetchingOrSubscribed.value = false
          console.timeEnd(perfLabel)
          reject(err)
        }
      })().catch(reject)
    })
  }

  // Unsubscribe (for tab visibility handling)
  const unsubscribeAll = () => {
    if (unsubscribeLeads) {
      unsubscribeLeads()
      unsubscribeLeads = null
    }
    isFetchingOrSubscribed.value = false
  }

  // Tab visibility handling — grace-delayed teardown (authConfig.tabSleepMinutes):
  // quick tab flips never unsubscribe; only a tab hidden past the grace pauses,
  // and the resume refetch runs only if a teardown actually happened.
  const setupVisibilityHandler = () => {
    if (visibilityHandler) return

    visibilityHandler = () => {
      if (document.hidden) {
        if (hiddenTeardownTimer || tornDownWhileHidden) return
        const graceMs = useAuthConfigStore().tabSleepMs
        hiddenTeardownTimer = setTimeout(() => {
          hiddenTeardownTimer = null
          tornDownWhileHidden = true
          console.log('[leadStore] Tab hidden past grace - pausing snapshots')
          unsubscribeAll()
        }, graceMs)
      } else {
        if (hiddenTeardownTimer) {
          clearTimeout(hiddenTeardownTimer)
          hiddenTeardownTimer = null
        }
        if (!tornDownWhileHidden) return // quick flip — snapshots never paused
        tornDownWhileHidden = false
        console.log('[leadStore] Tab visible - resuming snapshots')
        fetchLeads()
      }
    }

    document.addEventListener('visibilitychange', visibilityHandler)
  }

  const teardownVisibilityHandler = () => {
    if (visibilityHandler) {
      document.removeEventListener('visibilitychange', visibilityHandler)
      visibilityHandler = null
    }
    if (hiddenTeardownTimer) {
      clearTimeout(hiddenTeardownTimer)
      hiddenTeardownTimer = null
    }
    tornDownWhileHidden = false
  }

  // Update a lead
  const updateLead = async (leadId, payload) => {
    try {
      const docRef = doc(db, 'customers', leadId)
      await updateDoc(docRef, {
        ...payload,
        lastActivityTimestamp: serverTimestamp(),
      })
    } catch (err) {
      console.error('❌ [leadStore] Update lead error:', err)
      throw err
    }
  }

  // Computed: Group leads by temperature (reads from customerWorkflows)
  const leadsByTemperature = computed(() => {
    const groups = {
      hot: [],
      warm: [],
      cool: [],
      cold: [],
      dead: [],
      unknown: [],
    }

    const cwfStore = useCustomerWorkflowsStore()
    const taskStore = useWorkflowTasksStore()
    const tempTask = (taskStore.allTasks || []).find(t => t.displayName === 'Customer Temperature')
    const tempTaskId = tempTask?.id

    leads.value.forEach(lead => {
      const cwf = cwfStore.getWorkflow(lead.id)
      const temp = (
        (tempTaskId && findTaskStateEntry(cwf?.taskState, tempTaskId)?.value) ||
        ''
      ).toLowerCase()
      if (groups[temp]) {
        groups[temp].push(lead)
      } else {
        groups.unknown.push(lead)
      }
    })

    return groups
  })

  // Computed: Lead counts
  const leadCounts = computed(() => ({
    total: leads.value.length,
    hot: leadsByTemperature.value.hot.length,
    warm: leadsByTemperature.value.warm.length,
    cool: leadsByTemperature.value.cool.length,
    cold: leadsByTemperature.value.cold.length,
    dead: leadsByTemperature.value.dead.length,
  }))

  return {
    // State
    leads,
    leadsById,
    modifiedLeadIds,
    isLoading,
    error,
    lastFetched,
    hasInitialData,

    // Computed
    leadsByTemperature,
    leadCounts,

    // Actions
    fetchLeads,
    unsubscribeAll,
    updateLead,
    setupVisibilityHandler,
    teardownVisibilityHandler,
  }
})
